import { observable, action } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { removeSingleAlignmentLayer, showSingleAlignmentLayer } from '../AlignmentSummaryTable/AlignmentSummaryTableVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { DynamicAlignmentTableStore } from 'cd-eft/models/Stores/DynamicProfile/Alignment/DynamicAlignmentTableStore';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { AlignmentAoiController, AlignmentController, SetSurveyController, SurfaceDefController } from 'cd-eft/services/eftSetApi';
import { noSpecialChars } from 'cd-eft/utils/noSpecialChars';
import { GroundPoint } from '../../BreaklineTools/model/GroundPoint';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { FocusEvent } from 'react';
import { DynamicAlignmentMapStore } from 'cd-eft/models/Stores/DynamicProfile/Alignment/DynamicAlignmentMapStore';
import { TerraceDesignToolsController, WaterwayDesignToolsController} from 'cd-eft/services/eftSetApi';

const setStore = stores.AppStore.surveyEngineeringStore;
const alignmentStore = stores.AppStore.alignmentDesignStore;
const designStore = stores.AppStore.designStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;
const libraryStore = stores.AppStore.projectsLibraryStore;
const terraceDesignStore = stores.AppStore.terraceDesignStore;

export class IndependentAlignmentVM {
  public tableStore: DynamicAlignmentTableStore;
  @observable props;
  currentObject;
  private mounted: boolean = false;
  @observable autoGenerate: boolean = true; // auto-generate will be default
  @observable selectedDescription: string = 'Description';
  @observable uniqueDescriptions: string[] = [];
  private validSurveyPoints: object[] = [];
  @observable isShowTerraceChecked: boolean = false;

  constructor(props: any) {
    this.props = props;
    this.currentObject = alignmentStore.alignmentObject;
    this.currentObject.editingStatus = !alignmentStore.currentElement.id ? 'add' : 'update';
    this.currentObject.surfaceDefId = designStore.currentSurface.surfaceDefId;
    this.currentObject.surfaceDefTitle = designStore.currentSurface.surfaceDefTitle;
    this.currentObject.sessionId = designStore.projectsStore.currentSessionId;

    // prepare for disignProfileStore
    this.tableStore = props.profileTableStore;
    this.tableStore.setStore = setStore;
    this.tableStore.alignmentStore = alignmentStore;
    this.tableStore.CurrentObject = alignmentStore.alignmentObject;

    if (
      designStore.currentSurface.surfaceDefClassName === 'dem' ||
      this.currentObject.editingStatus === 'update'
    ) {
      this.isShowTerraceChecked = true;
      this.onChangeManualDigitize();
    } else {
      this.setUniqueDescriptions();
    }
  }

  @observable formValidation = {
    formErrors: {
      alignmentName: '',
    },
    alignmentNameValid: false,
    formValid: true,
  };
  @observable inputValue = '';
  @observable showCurveValueModal = false;
  @observable setSaveFlag = true;
  @observable curveValueModalState = {
    curveValue: undefined as number | undefined,
  };

  @action.bound openCurveValueModal(initialValue?: number) {
    this.curveValueModalState = {
      curveValue: initialValue,
    };
    this.showCurveValueModal = true;
  }

  @action.bound handleCloseCurveValueModal() {
    this.showCurveValueModal = false;
  }

  @action.bound handleCurveValueModalChange(value: number | undefined) {
    this.curveValueModalState.curveValue = value;
  }

  @action.bound handleApplyCurveValue() {
    if (this.curveValueModalState.curveValue === undefined || this.curveValueModalState.curveValue < 0) {
      return;
    }
    const curveValue = this.curveValueModalState.curveValue;

    this.currentObject.alignmentStations.forEach((station, index) => {
      if (index !== 0 && index !== this.currentObject.alignmentStations.length - 1) {
        if (curveValue > 0) {
          station.curveType = 2;
          station.curveValue = curveValue;
        } else {
          station.curveType = 1;
          station.curveValue = 0;
        }
      }
    });
    this.currentObject.alignmentApplyCurves = curveValue > 0 ? 'true' : 'false';
    if (this.currentObject.alignmentStations.length > 2) {
      this.updateCurves(undefined, false);
    }
    this.handleCloseCurveValueModal();
  }

  @action.bound onChangeAutoGenerate() {
    this.autoGenerate = true;
    this.isShowTerraceChecked = false;
    this.removeAllTerrces();
  }

  @action.bound onChangeManualDigitize(){
    (this.tableStore.hub.MapStore as DynamicAlignmentMapStore)?.clearPreviewPoints();
    this.autoGenerate = false;
    this.checkForTerraceLayers();
  }

  @action.bound async onChangeShowTerrace(e) {
    this.isShowTerraceChecked = e.target.checked;
    if (this.isShowTerraceChecked) {
      await loadAllTerraceShapesToMap();
    }
    else {
      this.removeAllTerrces();
    }
  }

  @action.bound async checkForTerraceLayers() {
    designStore.isLoading = true; 
    await loadAllTerraceShapesToMap();
    this.isShowTerraceChecked = stores.AppStore.terraceDesignStore.terraceLayers.length > 0 ? true : false;
    designStore.isLoading = false;
  }

  private removeAllTerrces() {
    const mapView = EngineeringMapService.instance?.mapView;
    if (mapView) {
      stores.AppStore.terraceDesignStore.terraceLayers.forEach((layer) => {
        mapView.map.remove(layer);
        layer.destroy();
      });
      stores.AppStore.terraceDesignStore.terraceLayers = [];
    }
  }
  @action.bound handleDeselectAllCurveValues() {
    this.currentObject.alignmentStations.forEach((station, index) => {
      if (index !== 0 && index !== this.currentObject.alignmentStations.length - 1) {
        station.curveType = 0;
        station.curveValue = 0;
      }
    });
    this.currentObject.alignmentApplyCurves = 'false';
    if (this.currentObject.alignmentStations.length > 2) {
      this.updateCurves(undefined, false);
    }
  }

  @action.bound applyCurveValueToSelectedStations() {
    const curveValue = this.curveValueModalState.curveValue;
    if (!curveValue || curveValue <= 0) return;
    this.currentObject.alignmentStations.forEach((station, index) => {
      if (
        alignmentStore.selectedAlignments.has(station.stationName) &&
        index !== 0 &&
        index !== this.currentObject.alignmentStations.length - 1
      ) {
        station.curveType = 2;
        station.curveValue = curveValue;
      }
    });
    this.currentObject.alignmentApplyCurves = 'true';
    if (this.currentObject.alignmentStations.length > 2) {
      this.updateCurves(undefined, false);
    }
    this.deselectAllStations();
  }

  @action.bound handleCheckboxChange(station: any) {
    if (station.stationName !== 'START' && station.stationName !== 'END') {
      const newSelectedAlignments = new Set(alignmentStore.selectedAlignments);
      if (newSelectedAlignments.has(station.stationName)) {
        newSelectedAlignments.delete(station.stationName);
      } else {
        newSelectedAlignments.add(station.stationName);
      }
      alignmentStore.selectedAlignments = newSelectedAlignments;
    }
  }

  @action.bound selectAllStations() {
    alignmentStore.selectedAlignments.clear();
    this.currentObject.alignmentStations.forEach((station, index) => {
      if (index !== 0 && index !== this.currentObject.alignmentStations.length - 1) {
        alignmentStore.selectedAlignments.add(station.stationName);
      }
    });
    alignmentStore.selectedAlignments = new Set(alignmentStore.selectedAlignments);
  }

  @action.bound deselectAllStations() {
    alignmentStore.selectedAlignments.clear();
    alignmentStore.selectedAlignments = new Set();
  }

  @action.bound updateSpecificStationCurveValue(station: any, curveValue: number) {
    if (curveValue === undefined) return;
    const selectedStations = this.currentObject.alignmentStations.filter(
      (s, index) =>
        index !== 0 &&
        index !== this.currentObject.alignmentStations.length - 1 &&
        alignmentStore.selectedAlignments.has(s.stationName)
    );
    selectedStations.forEach((s) => {
      s.curveType = curveValue === 0 ? 1 : 2;
      s.curveValue = curveValue;
    });
    this.updateCurves(undefined, false);
    this.deselectAllStations();
  }

  @action.bound onMount() {
    alignmentStore.resetSelectedAlignments();
    this.mounted = true;
    if (designStore.snappingEnabled) this.toggleAlignmentSnapping();
    this.props.setCallables({
      toggleSnapping: this.toggleAlignmentSnapping,
    });
  }

  @action.bound onUnmount() {
    this.mounted = false;
    alignmentStore.removeAlignmentLabelsLayer(EngineeringMapService.instance?.mapView);
    (this.tableStore.hub.MapStore as DynamicAlignmentMapStore)?.clearPreviewPoints();
  }

  @action.bound noProjecMessage(history) {
    MessageCenter.addWarning(designStore.noProjectMessage, [
      {
        callback: () => {
          history?.push('/projects/new-project');
          MessageCenter.clearMessages();
        },
        text: 'Go Create a Project',
      },
    ]);
  }

  @action.bound async setUniqueDescriptions() {
    const currentSessionUuid = designStore.projectsStore.currentProject.sessionList[0].sessionUuid;
    if (currentSessionUuid) {
      const allpoints: any[] = [];

      // Get survey points
      const response: any = await SurfaceDefController.getSurfaceDefBySessionUsingGET(currentSessionUuid);
      if (response && response.data.length > 0 && response.data[0].surveyMetadata?.length > 0) {
        for (const survey of response.data[0].surveyMetadata) {
          const response2 = await SetSurveyController.getSurveyUsingGET(survey.surveyId);
          if (response2.data && response2.data.xyzArray?.length) {
            response2.data.xyzArray.forEach((point) => {
              allpoints.push(point);
            });
          }
        }
      }

      if (allpoints.length > 0) {
        // Record how many survey points belong to each unique description
        const descriptionCounts = allpoints.reduce((acc, item: any) => {
          acc[item.featureDescription] = (acc[item.featureDescription] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        // Store only descriptions that belong to at least two survey points
        const tempArray = [
          ...new Set(
            allpoints
              .filter((item) => descriptionCounts[item.featureDescription] > 1)
              .map((item: any) => item.featureDescription)
          ),
        ] as string[];
        this.uniqueDescriptions = tempArray.sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );

        // Store valid survey points
        this.validSurveyPoints = allpoints.filter((item) => descriptionCounts[item.featureDescription] > 1);
      }

      if (this.uniqueDescriptions.length < 1) this.autoGenerate = false;
    }
  }

  @action.bound handleDescriptionChange(e) {
    this.selectedDescription = e.target.value;
    const points = this.getSelectedPoints();
    (this.tableStore.hub.MapStore as DynamicAlignmentMapStore)?.displayPreviewPoints(points);
  }

  @action.bound async autoGenerateAlignment(e) {
    e.preventDefault();
    const points = this.getSelectedPoints();
    this.tableStore.removeMapLayer('curved alignment');
    (this.tableStore.hub.MapStore as DynamicAlignmentMapStore)?.clearPreviewPoints();
    this.tableStore?.reset();
    this.tableStore.hub.ProfileStore?.reset(1);
    // designStore.autoProfileZoom = true;
    await (this.tableStore.hub.MapStore as DynamicAlignmentMapStore)?.autoGenerateAlignment(points);
  }

  private getSelectedPoints() {
    const selectedPoints: object[] = [];
    this.validSurveyPoints.forEach((item) => {
      if ((item as any).featureDescription === this.selectedDescription) {
        selectedPoints.push(item);
      }
    });

    return selectedPoints;
  }

  @action.bound setAlignmentName(e: any) {
    this.formValidation.formValid = false;
    const { name, value } = e.target;
    validateField(name, value, this.formValidation);
    alignmentStore.currentElement.name = value;
    this.currentObject.alignmentName = value;
  }

  // update AlignmentStations in Store
  @action.bound updateSingleAlignmentStation(e: any) {
    const value = e.target.value;
    const name = e.target.name;
    const index = e.target.getAttribute('data-i');

    if (name === 'x' || name === 'y') {
      if (Object.keys(designStore.originalStation).length === 0) {
        designStore.originalStation = Object.assign({}, this.currentObject.alignmentStations[index]);
      }
      const isNumber = Number(value);
      if (!isNaN(isNumber)) {
        this.currentObject.alignmentStations[index][name] = +value;
        alignmentStore.updateCurveSave = false;
      }
    } else {
      this.currentObject.alignmentStations[index][name] = +value;
      if (name === 'curveType') {
        if (value === '1' || value === '0') {
          this.currentObject.alignmentStations[index].curveValue = 0;
        }
      }
      if ((name as string).toLowerCase() === 'curvevalue') {
        this.tableStore.onCurveValueChanged(parseInt(index), parseFloat(value));
        if (parseFloat(value) === 0) {
          this.currentObject.alignmentStations[index].curveType = 1;
        }
      }
      alignmentStore.updateCurveSave = false;
    }
  }

  @action.bound updateMapAndProfile(e: any) {
    const index = e.target.getAttribute('data-i');
    this.tableStore.updateAlignment(this.tableStore.currentObject, index);
  }

  @action.bound setBeginningStation(e: any) {
    const inputValue = e.target.value;
    const beginningStationValue = inputValue === '' ? 0 : parseFloat(inputValue);
    this.inputValue = inputValue;
    alignmentStore.begginingStation = this.inputValue;
    const prevBeginningStationValue = this.currentObject.alignmentStations[0]?.stationName || 0;
    const difference = beginningStationValue - prevBeginningStationValue;

    this.currentObject.beginningStationDis = Number(this.inputValue);
    this.currentObject.alignmentStations.forEach((station, index) => {
      if (index === 0) {
        station.stationName = beginningStationValue;
        if (this.currentObject.alignmentStations.length > 1) {
          station.stationDistance = beginningStationValue;
        }
      } else {
        station.stationName = Math.round((parseFloat(station.stationName) || 0) + difference);
        if (this.currentObject.alignmentStations.length > 1) {
          station.stationDistance = (parseFloat(station.stationDistance) || 0) + difference;
        }
      }
    });
  }

  @action.bound sketchAlignment(e: any) {
    libraryStore.resetZValue = false;
    e.preventDefault();
    removeSingleAlignmentLayer(`alignment${this.currentObject.designElementId}`);
    this.tableStore.removeMapLayer('curved alignment');
    this.tableStore.sketchPolyline();
  }

  @action.bound toggleAlignmentSnapping() {
    if (this.mounted) this.tableStore.toggleAlignmentSnapping();
  }

  @action.bound async insertStation(e: any) {
    MessageCenter.clearMessages();
    e.preventDefault();

    if (this.currentObject.alignmentStations.length < 2) {
      await MessageCenter.addErrorFromCode('Alignment_21'); // You must create an alignment before inserting a station.
      return false;
    }
    if (this.currentObject.alignmentApplyCurves === 'true') {
      this.tableStore.insertStation(this.applyDefaultCurves);
    } else {
      this.tableStore.insertStation();
    }
  }

  @action.bound moveStation(e: any) {
    e.preventDefault();
    if (this.currentObject.alignmentApplyCurves === 'true') {
      this.tableStore.moveStation(this.updateCurves);
    } else {
      this.tableStore.moveStation();
    }
  }

  @action.bound deleteStation(e: any) {
    e.preventDefault();
    if (this.currentObject.alignmentStations.length > 2) {
      this.tableStore.deleteStationFromMapClick(setAlignmentStationToDelete);
    } else {
      MessageCenter.addErrorFromCode('Alignment_06');
    }
  }

  // apply curve -->
  @action.bound async applyDefaultCurves(e?: any) {
    if (e) e.preventDefault();
    applyDefaultCurvesHandler(this.tableStore, this.currentObject);
  }

  @action.bound async updateCurves(e?: any, setSaveFlag: boolean = true) {
    this.setSaveFlag = setSaveFlag;
    updateCurvesHandler(this.tableStore, this.currentObject, false, e, this.setSaveFlag);
  }

  @action.bound async saveIndependentAlignment(e: any, returnToSummary: boolean = false) {
    e.preventDefault();
    this.removeAllTerrces();
    const curves = this.currentObject.alignmentStations.filter((station) => {
      return station.curveValue > 0;
    });
    if (curves.length > 0 && !alignmentStore.updateCurveSave) {
      await MessageCenter.addErrorFromCode('Alignment_23');
    } else {
      alignmentStore.updateCurveSave = true;
      this.tableStore.clearDeleteStation();
      //alignmentStore.removeAlignmentLabelsLayer(EngineeringMapService.instance?.mapView);
      // designStore.autoProfileZoom = true;
      designStore.isLoading = true;

      this.tableStore.completeMapSketch();
      const noDuplicates = await checkDuplicates(this.currentObject);
      const countIsValid = await checkCountForSave(this.currentObject);
      // const noClosedLoop = await this.checkSelfClosed();
      // const noSelfCrossed = await this.checkSelfCrossed();
      const checkNameDuplicateResult = await checkNameDuplicate(this.currentObject, this.formValidation);
      if (noDuplicates && countIsValid && this.formValidation.formValid && !checkNameDuplicateResult) {
        // reset the Z values if the user drawing 2D alignment
        if (libraryStore.resetZValue) {
          this.currentObject.alignmentStations = this.currentObject.alignmentStations.map((station) => ({
            ...station,
            z: 0,
          }));
        }
        // save alignment
        const postJSON = { ...this.currentObject };

        const validApplyCurvesToAlignment = await applyCurvesToAlignment(this.tableStore, postJSON);
        if (validApplyCurvesToAlignment) {
          if (alignmentStore.unsavedAlignments.has(postJSON.designElementId)) postJSON.designElementId = 0;
          if (postJSON.alignmentXY) delete postJSON.alignmentXY;
          if (postJSON.curvedAlignmentXY) delete postJSON.curvedAlignmentXY;
          await addDesignElementJsonForAlignment(postJSON, false, returnToSummary);
          // generate AOI
          const data = this.currentObject.alignmentStations;
          await createAlignmentAOI(data);
          if (returnToSummary) {
            this.tableStore.cleanUpMap();
            alignmentStore.begginingStation = '0';
          } else {
            this.currentObject = alignmentStore.alignmentObject;
            this.currentObject.editingStatus = 'update';
            this.tableStore.alignmentStore = alignmentStore;
            this.tableStore.CurrentObject = alignmentStore.alignmentObject;
            this.currentObject.designElementId = alignmentStore.currentElement.id;
            removeSingleAlignmentLayer(`alignment${this.currentObject.designElementId}`);
          }
        }
      }
    }
    if (returnToSummary) alignmentStore.begginingStation = '0';
    designStore.isLoading = false;
  }

  @action.bound deleteSingleAlignmentStation = () => {
    deleteSingleAlignmentStationHandler(this.tableStore, this.currentObject);
  };

  // <-- delete single alignment station
  @action.bound cancelEditIndependentAlignment(e: any) {
    this.removeAllTerrces();
    e.preventDefault();
    this.tableStore.clearDeleteStation();
    // designStore.autoProfileZoom = true;
    designStore.disableInput = true;
    if (
      alignmentStore.unsavedAlignments.size > 0 &&
      alignmentStore.unsavedAlignments.has(this.currentObject.designElementName)
    ) {
      this.currentObject.designElementId = this.currentObject.designElementName;
    }

    showSingleAlignmentLayer(this.currentObject.designElementId, '');
    removeSingleAlignmentLayer(`alignment${this.currentObject.designElementId}`);
    this.tableStore.completeMapSketch();
    this.tableStore.cleanUpMap();
    if (alignmentStore.designElementsDataMap.has(this.currentObject.designElementId)) {
      const originalJsonData = alignmentStore.designElementsDataMap.get(this.currentObject.designElementId);
      showSingleAlignmentLayer(originalJsonData, '');
    }
    alignmentStore.displayedAlignments.add(this.currentObject.designElementId);
    alignmentStore.resetCurrentObject();
    alignmentStore.currentTab = 'Summary';
    alignmentStore.begginingStation = '0';
  }

  @action.bound async checkCountForSave() {
    if (this.currentObject.alignmentStations.length < 2) {
      await MessageCenter.addErrorFromCode('Alignment_06'); // An alignment requires at least two alignment stations.
      return false;
    }
    return true;
  }

  handleRowMouseEnter(station) {
    const point = new GroundPoint();
    point.id = station.stationName;
    point.name = station.stationName;
    point.x = station.x;
    point.y = station.y;
    point.z = station.z;
    point.wkid = appConfig.tempHardCodes.wkid;
    EngineeringMapService.instance?.trackPoint(point);
  }

  handleRowMouseLeave(station) {
    EngineeringMapService.instance?.unTrackLast();
  }

  handleCurveValueInputFocus = (e: FocusEvent<HTMLInputElement>, index: number) => {
    const curveValue = e.target.value;
    this.tableStore.focusStationIndex = index;
    if (0 < index && index < this.currentObject.alignmentStations.length - 1) {
      this.tableStore.onCurveValueChanged(index, parseFloat(curveValue));
      EngineeringMapService.instance?.unTrackLast();
      // console.log(this.currentObject.alignmentStations[index]);
      this.currentObject.alignmentStations[index].curveType = 2;
    }
  };
}

export const createAlignmentAOI = async (data: any) => {
  const postJSONforAOI = {
    projectUuid: designStore.projectsStore.currentProject.projectUuid.toUpperCase(),
    surveyCoordinateSystem: String(appConfig.tempHardCodes.wkid),
    alignmentStations: data,
  };
  try {
    alignmentStore.isLoading = true;

    await AlignmentAoiController.updateProjectAlignmentAOIUsingPOST(postJSONforAOI);
  } catch (error) {
    MessageCenter.addError((error as Error).message);
  } finally {
    alignmentStore.isLoading = false;
  }
};

export const addDesignElementJsonForAlignment = async (postJSON: any, fromTerrace: boolean = false, returnToSummary: boolean = false) => {
  try {
    const response: any = await AlignmentController.addDesignElementJsonForAlignmentUsingPOST(postJSON);

    const oldId = postJSON.designElementId ? postJSON.designElementId : postJSON.designElementName;
    const newJson = response.data;
    newJson.designElementId = response.data.designElementId;

    if (!fromTerrace) {
      // make changes to summary and tracking
      removeAignmentFromSummary(oldId);
      addAignmentToSummary(newJson);

      if (returnToSummary) {
        alignmentStore.resetCurrentObject();
      } else {
        alignmentStore.currentElement.id = newJson.designElementId;
        alignmentStore.currentElement.name = newJson.designElementName;
        alignmentStore.currentElement.type = newJson.designElementTypeId;
      }
    }
  } catch (error) {
    MessageCenter.addError((error as Error).message);
  }
};

export const addAignmentToSummary = async (alignment) => {
  // 1. add to summary array
  const shortAlignment = {
    designElementId: alignment.designElementId,
    designElementName: alignment.designElementName,
    designElementTypeId: alignment.designElementTypeId,
    sessionId: alignment.sessionId,
  };
  alignmentStore.summaryTable.unshift(shortAlignment);
  // 2. add to designElementsDataArray
  alignmentStore.designElementsDataArray.unshift(alignment);
  // 3. add to designElementsDataMap
  alignmentStore.designElementsDataMap.set(alignment.designElementId, alignment);
  // 4. add to tracking add to map
  showSingleAlignmentLayer(alignment.designElementId);
};

export const removeAignmentFromSummary = async (oldId) => {
  // 1. remove from summary array
  const newsummaryTable = alignmentStore.summaryTable.filter((el) => el.designElementId !== oldId);
  alignmentStore.summaryTable = newsummaryTable;
  // 2. remove from designElementsDataArray
  const newdesignElementsDataArray = alignmentStore.designElementsDataArray.filter(
    (el) => el.designElementId !== oldId
  );
  alignmentStore.designElementsDataArray = newdesignElementsDataArray;
  // 3. remove from designElementsDataMap
  if (alignmentStore.designElementsDataMap.has(oldId)) alignmentStore.designElementsDataMap.delete(oldId);
  // 4. remove from tracking
  if (alignmentStore.selectedAlignments.has(oldId)) {
    alignmentStore.selectedAlignments.delete(oldId);
  }
  if (alignmentStore.displayedAlignments.has(oldId)) {
    alignmentStore.displayedAlignments.delete(oldId);
  }
  if (alignmentStore.unsavedAlignments.has(oldId)) {
    alignmentStore.unsavedAlignments.delete(oldId);
  }
  // 5. remove from map
  if (gisModel.map) {
    removeSingleAlignmentLayer(`alignment${oldId}`);
  }
};

export const checkNotLastStation = async (currentObject) => {
  if (currentObject.alignmentStations.length === 1) {
    /*
   Add flow: Alignment_18	-	An alignment needs one point. Please cancel the alignment and redraw.
   Update flow:  Alignment_19	- An alignment needs one point. To delete this alignment, Cancel the alignment edit and return to the summary panel and choose delete.
    */
    await MessageCenter.addWarningFromCode(currentObject.editingStatus === 'add' ? 'Alignment_18' : 'Alignment_19');
    return false;
  }
  return true;
};

// delete single alignment station -->
export const setAlignmentStationToDelete = async (stationName: number, e?: any) => {
  e?.preventDefault();
  const notLastStation = await checkNotLastStation(alignmentStore.alignmentObject);
  if (notLastStation) {
    alignmentStore.alignmentStationToDelete.stationName = stationName;
    alignmentStore.toggleDeleteAlStDialog();
  }
};

export const checkCurveValue = async (processStatus) => {
  if (processStatus.substring(0, 7) === 'Failure') {
    const errorDetails = processStatus.substring(processStatus.indexOf(' ') + 1);
    const messageObject = await MessageCenter.getMessageFromCode_object('Alignment_09', 'error', []);
    MessageCenter.addError(`${messageObject.messageContent}\n${errorDetails}`, [], 'Alignment_09');
    return false;
  }
  return true;
};

export const checkDuplicates = async (currentObject) => {
  const coordinatesMap = new Map();
  for (const el of currentObject.alignmentStations) {
    if (coordinatesMap.has(el.x) && coordinatesMap.get(el.x) === el.y) {
      await MessageCenter.addErrorFromCode('Alignment_02'); // Duplicate alignment stations.
      return false;
    } else {
      coordinatesMap.set(el.x, el.y);
    }
  }
  return true;
};

export const deleteSingleAlignmentStationHandler = async (tableStore, currentObject, fromTerrace: boolean = false) => {
  alignmentStore.toggleDeleteAlStDialog();
  const indexToDelete = currentObject.alignmentStations.findIndex(
    (s) => s.stationName === alignmentStore.alignmentStationToDelete.stationName
  )
  tableStore.DeleteStation(indexToDelete, currentObject, true, fromTerrace, tableStore);
};

// Updata Curve After Point Deleted
export const updateCurveAfterPointDeleted = async (tableStore, currentObject, fromTerrace: boolean = false) => {
  designStore.isLoading = true;
  delete currentObject.curvedAlignmentXY;
  delete currentObject.alignmentXY;
  const postJSON = { ...currentObject };
  // last and first station bring to 0
  currentObject.alignmentStations[0].curveValue = 0;
  currentObject.alignmentStations[0].curveType = 0;
  const lastStationIndex = currentObject.alignmentStations.length - 1;
  currentObject.alignmentStations[lastStationIndex].curveValue = 0;
  currentObject.alignmentStations[lastStationIndex].curveType = 0;
  await applyCurvesToAlignment(tableStore, postJSON, fromTerrace);
  designStore.isLoading = false;
};

export const applyCurvesToAlignment = async (tableStore, postJSON, fromTerrace: boolean = false) => {
  delete postJSON.alignmentXY;
  delete postJSON.curvedAlignmentXY;
  try {
    alignmentStore.isLoading = true;
    const response: any = await AlignmentController.applyDefaultCurvesToAlignmentUsingPOST(postJSON);
    const validCurveValue = await checkCurveValue(response.data.processStatus);
    if (!validCurveValue) {
      return false;
    }
    const responseJson = JSON.parse(response.data.json);
    alignmentStore.alignmentObject.alignmentStations = [...responseJson.alignmentStations];
    alignmentStore.alignmentObject.alignmentXY = [...responseJson.alignmentXY];
    let curve = responseJson.curvedAlignmentXY;
    if (response && response.data.profileDTO) {
      if (response.data.profileDTO.stationDTOList) {
        const curveProfile = response.data.profileDTO.stationDTOList;
        const curvedAlignmentXY: { id; x; y; z }[] = [];
        let id = 1;
        for (const station of curveProfile) {
          const point = {
            id: id,
            x: station.inputPoint?.x,
            y: station.inputPoint?.y,
            z: station.inputPoint?.z,
          };
          id++;
          curvedAlignmentXY.push(point);
        }
        curve = curvedAlignmentXY;
        if (fromTerrace) designStore.alignmentProfileStations = curveProfile;
      }
    }

    if (fromTerrace) {
      tableStore.DrawCurve(curve);
      await tableStore.hub.ProfileStore?.redrawMappingProfiles();
      await tableStore.hub.ProfileStore?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
    } else if (postJSON.alignmentApplyCurves === "true") {
      tableStore.DrawCurve(curve);
      tableStore.drawCurvedProfile(curve);
    }
    return true;
  } catch (error) {
    MessageCenter.addError((error as Error).message + '. Try to reduce curve values.');
  } finally {
    alignmentStore.isLoading = false;
  }
};
// <-- apply curve

export const checkCountForCurve = async (currentObject) => {
  if (currentObject.alignmentStations.length < 3) {
    await MessageCenter.addErrorFromCode('Alignment_01'); // Alignment stations must be greater than 3 for the default curves.
    return false;
  }
  return true;
};

export const checkCountForSave = async (currentObject) => {
  if (currentObject.alignmentStations.length < 2) {
    await MessageCenter.addErrorFromCode('Alignment_06'); // An alignment requires at least two alignment stations.
    return false;
  }
  return true;
};

export const updateCurvesHandler = async (tableStore, currentObject, fromTerrace: boolean = false, e?: any, setSaveFlag: boolean = true) => {
  if (e) e.preventDefault();
  tableStore.clearDeleteStation();
  const noDuplicates = await checkDuplicates(currentObject);
  const countIsValid = await checkCountForCurve(currentObject);
  if (noDuplicates && countIsValid) {
    currentObject.alignmentStations.forEach(station => {
      if (station.curveValue === 0) {
        station.curveType = 1;
      }
    });
    if (currentObject.alignmentStations.reduce((acc, el) => acc + el.curveValue, 0) === 0) {
      currentObject.alignmentApplyCurves = 'false';
    }
    const data = { ...currentObject };
    if (
      alignmentStore.unsavedAlignments.size > 0 &&
      alignmentStore.unsavedAlignments.has(currentObject.designElementId)
    ) {
      data.designElementId = 0;
    }
    await applyCurvesToAlignment(tableStore, data, fromTerrace);
    if (setSaveFlag) {
      alignmentStore.updateCurveSave = true;
    } else {
      alignmentStore.updateCurveSave = false;
    }
  }
  alignmentStore.disableSaveAlignment = false;
};

export const applyDefaultCurvesHandler = async (tableStore, currentObject, fromTerrace: boolean = false, e?: any) => {
  if (e) e.preventDefault();
  tableStore.focusStationIndex = -1;
  tableStore.clearCurveCircle();
  tableStore.clearDeleteStation();
  const noDuplicates = await checkDuplicates(currentObject);
  const countIsValid = await checkCountForCurve(currentObject);
  if (noDuplicates && countIsValid) {
    alignmentStore.alignmentObject.alignmentApplyCurves = 'true';
    currentObject.alignmentStations.forEach((el, i) => {
      if (i !== 0 && i !== currentObject.alignmentStations.length - 1) {
        el.curveType = 2;
        el.curveValue = 50;
      }
    });
    const data = { ...currentObject };
    if (
      alignmentStore.unsavedAlignments.size > 0 &&
      alignmentStore.unsavedAlignments.has(currentObject.designElementId)
    ) {
      data.designElementId = 0;
    }
    await applyCurvesToAlignment(tableStore, data, fromTerrace);
  }
  alignmentStore.disableSaveAlignment = false;
};

export const checkNameDuplicate = async (currentObject, formValidation) => {
  const nameDuplicate = alignmentStore.summaryTable.some((summary) => {
    return (
      summary.designElementName.toLowerCase() === currentObject.alignmentName.toLowerCase() &&
      currentObject.designElementId !== summary.designElementId
    );
  });
  if (nameDuplicate) {
    const errorCode = 'Common_07';
    const fieldName = 'alignmentName';
    await getErrorMessage(errorCode, formValidation.formErrors, fieldName);
    return true;
  }
  return false;
};

export const getErrorMessage = async (code: string, container, fieldName: string) => {
  if (code === '') {
    container[fieldName] = '';
  } else {
    const message = await MessageCenter.addErrorLabelFromCode(code);
    container[fieldName] = message;
  }
  return;
};

export const validateField = async (fieldName, value, formValidation) => {
  const keyValid = fieldName + 'Valid';
  let fieldValid = formValidation[keyValid];
  const fieldValidationErrors = formValidation.formErrors;
  let errorCode: string = '';
  switch (fieldName) {
    case 'alignmentName':
      fieldValid = value !== '' && noSpecialChars(value);
      errorCode = fieldValid ? '' : 'Alignment_20';
      break;
  }
  formValidation.formErrors = fieldValidationErrors;
  await getErrorMessage(errorCode, formValidation.formErrors, fieldName);
  formValidation[keyValid] = fieldValid;
  validateForm(formValidation);
};

export const validateForm = (formValidation) => {
  let valid = true;
  for (const key in formValidation.formErrors) {
    if (!formValidation[key + 'Valid']) {
      valid = false;
    }
  }
  formValidation.formValid = valid;
};

export const loadAllTerraceShapesToMap = async (): Promise<void> => {
  const sessionId = stores.AppStore.designStore.projectsStore.currentSessionId;
  const mapView = EngineeringMapService.instance?.mapView;

  if (!sessionId || !mapView) {
    console.warn('Missing session ID or map view');
    return;
  }

  try {
    const response = await TerraceDesignToolsController.getTerraceDesignSummariesBySessionUuidUsingGET(sessionId);
    const data = response?.data ?? [];

    // Clear existing terrace layers
    stores.AppStore.terraceDesignStore.terraceLayers.forEach(layer => {
      mapView.map.remove(layer);
      layer.destroy();
    });
    stores.AppStore.terraceDesignStore.terraceLayers = [];

    for (const e of data) {
      const terraceId = e.alignedElementDesignParamsId ? e.alignedElementDesignParamsId : -1;
      const terraceTitle = e.name ? e.name : `Terrace ${terraceId}`;

      if (terraceId !== -1) {
        const shapeResponse = await WaterwayDesignToolsController.getAlignedElementShapeByAlignedElementIdUsingGET(terraceId);

        if (shapeResponse?.data?.alignedFeatureDesignShapeJson) {
          const channelResult = JSON.parse(shapeResponse.data.alignedFeatureDesignShapeJson);
          if (channelResult?.terraceStructureShapeDTO) {
            stores.AppStore.terraceDesignStore.addTerraceToMap(
              channelResult.terraceStructureShapeDTO,
              mapView,
              terraceId,
              terraceTitle
            );
          }
        } else {
          console.error(`No shape data for terrace ID ${terraceId}`);
        }
      }
    }

  } catch (error) {
    console.error('Failed to load terrace shapes:', error);
    MessageCenter.addError((error as Error).message);
  }
};
